import { CardTable, Link, TableBottomCell } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  Flex,
  CurrencyValue,
  WalletAddress,
  RelativeDateTime,
  TableSkeleton
} from '@blockanalitica/ui';
import { PaginatedResponse, useApi } from '@/api';

type LiquidationsData = {
  address: string;
  market_address: string;
  name: string;
  underlying_symbol: string;
  market_symbol: string;
  market_underlying_address: string;
  wallet_address: string;
  collateral_amount_usd: string | null;
  debt_amount: string | null;
  debt_amount_usd: string | null;
  datetime: string;
  liquidation_id: string;
  network: string;
  collaterals: {
    amount: string | null;
    amount_usd: string | null;
    underlying_symbol: string;
    underlying_address: string;
  }[];
};

export default function LiquidationsTable({
  marketAddress,
  network
}: {
  marketAddress: string;
  network: string;
}) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-datetime'
  });

  const [data, error] = useApi<PaginatedResponse<LiquidationsData[]>>({
    url: `/markets/${marketAddress}/liquidations/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<LiquidationsData>[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`../borrowers/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Collateral Assets',
      getCell: (row) => (
        <Flex flexDirection="column" gap="2xs">
          {row.collaterals.map((collateral) => (
            <Flex
              key={collateral.underlying_address}
              gap="3xs"
              alignItems="center">
              <CurrencyValue
                value={collateral.amount}
                options={{
                  currency: {
                    name: collateral.underlying_symbol,
                    address: collateral.underlying_address,
                    network: network
                  }
                }}
              />
              /
              <CurrencyValue
                value={collateral.amount_usd}
                variant="muted"
                options={{
                  currency: 'USD',
                  notation: 'compact'
                }}
              />
            </Flex>
          ))}
        </Flex>
      ),
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    },
    {
      header: 'Total Collateral Seized',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_amount_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      sortingField: 'collateral_amount_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.debt_amount}
          options={{
            currency: {
              name: row.market_symbol,
              address: row.market_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell>
          <CurrencyValue
            value={row.debt_amount_usd}
            variant="muted"
            options={{
              currency: 'USD'
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'debt_amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <RelativeDateTime
          value={row.datetime}
          formatDateTimeOptions={{ toRelativeUnit: 'days' }}
        />
      ),
      sortingField: 'datetime',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <CardTable<LiquidationsData>
      title="Liquidations"
      rows={data['results']}
      columns={columns}
      getKey={(row) => row.liquidation_id}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
